// Modale création / édition d'une tâche (admin_tasks).
// Champs : titre, note, scope, logement (prop_id AUTO-déduit), proprio si
// scope=proprio, priorité, étiquettes multi, échéance (date + heure optionnelle
// via due_all_day).

import { useState } from 'react'
import { DS } from '../../lib/ui'
import { getAllLogsFlat } from '../../lib/sousloc-helpers'
import {
  SCOPES,
  SCOPE_LABELS,
  PRIORITIES,
  PRIORITY_LABELS,
  deduceProporId,
} from './taches-logic'

// Découpe un timestamptz en { date:'YYYY-MM-DD', time:'HH:MM' } locale.
function splitDue(dueAt) {
  if (!dueAt) return { date: '', time: '' }
  const d = new Date(dueAt)
  if (Number.isNaN(d.getTime())) return { date: '', time: '' }
  const pad = n => String(n).padStart(2, '0')
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  }
}

export default function TaskModal({ task, cfg, labels, onSave, onClose, onArchive, onDelete }) {
  const isEdit = !!task?.id
  const initDue = splitDue(task?.due_at)

  const [title, setTitle] = useState(task?.title || '')
  const [note, setNote] = useState(task?.note || '')
  const [scope, setScope] = useState(task?.scope || 'divers')
  const [logementId, setLogementId] = useState(task?.logement_id || '')
  const [propId, setPropId] = useState(task?.prop_id || '')
  const [priority, setPriority] = useState(task?.priority || 'normale')
  const [selLabels, setSelLabels] = useState(task?.labels || [])
  const [dueDate, setDueDate] = useState(initDue.date)
  const [dueTime, setDueTime] = useState(task?.due_all_day === false ? initDue.time : '')
  const [saving, setSaving] = useState(false)

  const logs = getAllLogsFlat(cfg)
  const props = cfg?.props || []

  const onChangeLogement = (id) => {
    setLogementId(id)
    // prop_id AUTO-déduit depuis cfg (jamais saisi à la main pour scope logement).
    setPropId(deduceProporId(cfg, id) || '')
  }

  const toggleLabel = (id) => {
    setSelLabels(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  const buildDueAt = () => {
    if (!dueDate) return { due_at: null, due_all_day: true }
    if (dueTime) {
      // date + heure → timestamp local
      return { due_at: new Date(`${dueDate}T${dueTime}`).toISOString(), due_all_day: false }
    }
    // jour entier → midi local (évite les sauts de jour par timezone)
    return { due_at: new Date(`${dueDate}T12:00`).toISOString(), due_all_day: true }
  }

  const handleSave = async () => {
    if (!title.trim()) return
    setSaving(true)
    const due = buildDueAt()
    const patch = {
      title: title.trim(),
      note: note.trim() || null,
      scope,
      logement_id: scope === 'logement' ? (logementId || null) : null,
      prop_id: scope === 'logement' ? (propId || null) : scope === 'proprio' ? (propId || null) : null,
      priority,
      labels: selLabels,
      due_at: due.due_at,
      due_all_day: due.due_all_day,
    }
    const ok = await onSave(task?.id, patch)
    setSaving(false)
    if (ok) onClose()
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 18, color: DS.marineDark, marginBottom: 14 }}>
        {isEdit ? 'Modifier la tâche' : 'Nouvelle tâche'}
      </div>

      <Field label="Titre *">
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          autoFocus
          style={inputStyle}
          data-testid="task-modal-title"
        />
      </Field>

      <Field label="Note">
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </Field>

      <Row>
        <Field label="Contexte">
          <select value={scope} onChange={e => setScope(e.target.value)} style={inputStyle} data-testid="task-modal-scope">
            {SCOPES.map(s => <option key={s} value={s}>{SCOPE_LABELS[s]}</option>)}
          </select>
        </Field>
        <Field label="Priorité">
          <select value={priority} onChange={e => setPriority(e.target.value)} style={inputStyle}>
            {PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
          </select>
        </Field>
      </Row>

      {scope === 'logement' && (
        <Field label="Logement (proprio déduit auto)">
          <select value={logementId} onChange={e => onChangeLogement(e.target.value)} style={inputStyle} data-testid="task-modal-logement">
            <option value="">— choisir —</option>
            {logs.map(l => (
              <option key={l.id} value={l.id}>{l.nom}{l.propNom ? ` · ${l.propNom}` : ''}</option>
            ))}
          </select>
        </Field>
      )}

      {scope === 'proprio' && (
        <Field label="Proprio">
          <select value={propId} onChange={e => setPropId(e.target.value)} style={inputStyle} data-testid="task-modal-proprio">
            <option value="">— choisir —</option>
            {props.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
          </select>
        </Field>
      )}

      <Row>
        <Field label="Échéance (date)">
          <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Heure (optionnel)">
          <input
            type="time"
            value={dueTime}
            onChange={e => setDueTime(e.target.value)}
            disabled={!dueDate}
            style={{ ...inputStyle, opacity: dueDate ? 1 : 0.5 }}
          />
        </Field>
      </Row>

      <Field label="Étiquettes">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {labels.length === 0 && <span style={{ fontSize: 12, color: DS.textLight }}>Aucune étiquette (créer via le gestionnaire)</span>}
          {labels.map(l => {
            const active = selLabels.includes(l.id)
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => toggleLabel(l.id)}
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '4px 10px',
                  borderRadius: 12,
                  cursor: 'pointer',
                  background: active ? `${l.color}22` : 'transparent',
                  color: active ? l.color : DS.textLight,
                  border: `1.5px solid ${active ? l.color : '#ccd'}`,
                }}
              >
                {l.emoji ? `${l.emoji} ` : ''}{l.name}
              </button>
            )
          })}
        </div>
      </Field>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, gap: 8 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {isEdit && onArchive && !task.archived_at && (
            <button type="button" onClick={() => onArchive(task)} style={ghostBtn} data-testid="task-modal-archive">
              Archiver
            </button>
          )}
          {isEdit && onDelete && (
            <button type="button" onClick={() => onDelete(task)} style={{ ...ghostBtn, color: DS.danger, borderColor: `${DS.danger}55` }} data-testid="task-modal-delete">
              Supprimer
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={onClose} style={ghostBtn}>Annuler</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!title.trim() || saving}
            style={{
              ...primaryBtn,
              opacity: !title.trim() || saving ? 0.5 : 1,
              cursor: !title.trim() || saving ? 'default' : 'pointer',
            }}
            data-testid="task-modal-save"
          >
            {saving ? '…' : (isEdit ? 'Enregistrer' : 'Créer')}
          </button>
        </div>
      </div>
    </Overlay>
  )
}

function Overlay({ children, onClose }) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="fbz-modal-sheet"
        style={{ background: DS.white, borderRadius: 16, padding: 24, maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,.22)', fontFamily: DS.fontBody }}
      >
        {children}
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div style={{ flex: 1, marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: DS.navy, marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  )
}

function Row({ children }) {
  // fbz-row : 1 colonne < 768px (mobile), 2 colonnes ≥ 768px (desktop inchangé).
  return <div className="fbz-row">{children}</div>
}

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: DS.radiusControl,
  border: `0.5px solid ${DS.borderHover}`,
  fontSize: 14,
  fontFamily: DS.fontBody,
  color: DS.marineDark,
  boxSizing: 'border-box',
}

const ghostBtn = {
  fontSize: 13,
  fontWeight: 600,
  padding: '9px 15px',
  borderRadius: DS.radiusControl,
  border: `0.5px solid ${DS.borderHover}`,
  background: DS.card,
  color: DS.marine,
  cursor: 'pointer',
  fontFamily: DS.fontBody,
}

const primaryBtn = {
  fontSize: 13,
  fontWeight: 600,
  padding: '9px 16px',
  borderRadius: DS.radiusControl,
  border: 'none',
  background: DS.corail,
  color: '#fff',
  cursor: 'pointer',
  fontFamily: DS.fontBody,
}
